import {
	BASE_PRECISION_EXP,
	BigNum,
	PRICE_PRECISION_EXP,
	PerpMarketAccount,
	PositionDirection,
	SpotMarketAccount,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIOrderType,
	TradeOffsetPrice,
	UIMarket,
	ENUM_UTILS,
} from '@velocity-exchange/common';
import { UserSettings } from '../../../../environmentVariables/EnvironmentVariableTypes';
import Env from '../../../../environmentVariables/EnvironmentVariables';
import { V2AuctionIdentifierProps } from '../../../../utils/VelocityAppEvents';
import { isMajorMarket } from 'src/utils/market';
import { ITradeForm } from '../../types/tradeForm';
import { CommonTradeFormOutputOrderConstructionProps } from './types';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const getToastId = (
	marketId: MarketId,
	direction: PositionDirection,
	orderType: UIOrderType
) => {
	return `${marketId.key}-${orderType}-${ENUM_UTILS.toStr(
		direction
	)}-${Date.now()}`;
};

/**
 * The single canonical toast-props shape produced at the order-prep seam. The
 * perp-market, swift-market, and spot order flows all obtain their toast
 * identity from {@link TRADE_PREP_UTILS.getOrderToastProps} and adapt this one
 * shape to whichever event payload they emit — rather than each hand-building a
 * near-identical shape (or, in the swift-limit case, a bare toast-ID string).
 *
 * `getToastId` is the lone owner of the toast-ID string format, so adding e.g.
 * base amount to toast-ID matching later (see market-order-toast-refactor-goals.md
 * goal #2) is a one-place change in this module.
 */
export interface OrderToastProps {
	/** Identifier fields shared by every order-toast flow. */
	v2Props: V2AuctionIdentifierProps;
	/** Toast-ID string; single-owner format via `getToastId`. */
	toastId: string;
	/**
	 * Swift-only: original trade-form details that power the retry button on an
	 * expired signed-message order. Absent for the perp-legacy and spot flows.
	 */
	originalTradeFormDetails?: Partial<ITradeForm>;
}

export const TRADE_PREP_UTILS = {
	/**
	 * The one toast-props producer. Every market/limit order flow calls this so
	 * the toast-ID format and identifier shape have a single owner.
	 */
	getOrderToastProps: (props: {
		marketId: MarketId;
		direction: PositionDirection;
		baseSize: BigNum;
		numOrders: number;
		currentSettings: Partial<UserSettings>;
		bracketOrders?: CommonTradeFormOutputOrderConstructionProps['bracketOrders'];
		orderType: CommonTradeFormOutputOrderConstructionProps['orderType'];
		userOrderId: number;
		tradeFormDetails?: Partial<ITradeForm>;
	}): OrderToastProps => {
		return {
			v2Props: {
				marketId: props.marketId,
				direction: props.direction,
				baseAmountOrdered: props.baseSize.val,
				numOrders: props.numOrders,
				// Only perp flows surface auction progress in the toast; spot
				// market-order toasts intentionally skip the auction stage. Keying
				// off the market type lets this single producer reproduce each
				// flow's historical `auctionEnabled` value.
				auctionEnabled:
					props.marketId.isPerp &&
					(props.currentSettings?.auctionDurationSeconds ?? 0) > 0 &&
					!props.currentSettings?.placeAndTakeEnabled,
				includesSlOrder: !!props.bracketOrders?.stopLoss,
				includesTpOrder: !!props.bracketOrders?.takeProfit,
				userOrderId: props.userOrderId,
			},
			toastId: getToastId(
				props.marketId,
				props.direction,
				props.orderType as UIOrderType
			),
			originalTradeFormDetails: props.tradeFormDetails,
		};
	},
	perp: {
		formatTradeFormFieldValue: {
			baseSize: (input: string): BigNum => {
				return parseInputBigNumOrZero(input, BASE_PRECISION_EXP);
			},
			price: (input: string): BigNum => {
				return parseInputBigNumOrZero(input, PRICE_PRECISION_EXP);
			},
			secondaryPrice: (input: string): BigNum => {
				return parseInputBigNumOrZero(input, PRICE_PRECISION_EXP);
			},
		},
		adjustForStepSize: (
			baseSize: BigNum,
			perpMarketAccount: PerpMarketAccount
		) => {
			if (!perpMarketAccount) return baseSize;

			const stepSizeBn = perpMarketAccount.orderStepSize;
			const baseSizeBn = baseSize.val;
			const baseSizeAfterStep = baseSizeBn.div(stepSizeBn).mul(stepSizeBn);

			return BigNum.from(baseSizeAfterStep, BASE_PRECISION_EXP);
		},
	},
	spot: {
		formatTradeFormFieldValue: {
			baseSize: (
				input: string,
				spotMarketAccount: SpotMarketAccount
			): BigNum => {
				const basePrecisionToUse =
					UIMarket.spotMarkets[spotMarketAccount.marketIndex].precisionExp;

				return parseInputBigNumOrZero(input, basePrecisionToUse);
			},
			price: (input: string): BigNum => {
				return parseInputBigNumOrZero(input, PRICE_PRECISION_EXP);
			},
			secondaryPrice: (input: string): BigNum => {
				return parseInputBigNumOrZero(input, PRICE_PRECISION_EXP);
			},
		},
		adjustForStepSize: (
			baseSize: BigNum,
			spotMarketAccount: SpotMarketAccount
		) => {
			const basePrecisionToUse =
				UIMarket.spotMarkets[spotMarketAccount.marketIndex].precisionExp;

			const stepSizeBn = spotMarketAccount.orderStepSize;
			const baseSizeBn = baseSize.val;
			const baseSizeAfterStep = baseSizeBn.div(stepSizeBn).mul(stepSizeBn);

			return BigNum.from(baseSizeAfterStep, basePrecisionToUse);
		},
	},
	getMarketBasedAuctionStartPriceOffsetFrom: (
		settingsValue: TradeOffsetPrice | 'marketBased',
		marketId: MarketId
	): TradeOffsetPrice => {
		if (settingsValue !== 'marketBased') {
			return settingsValue;
		}

		return isMajorMarket(marketId) ? 'mark' : 'bestOffer';
	},
	getMarketBasedAuctionStartPriceOffset: (
		settingsValue: number | 'marketBased',
		marketId: MarketId
	): number => {
		if (settingsValue !== 'marketBased') {
			return settingsValue;
		}

		return isMajorMarket(marketId)
			? Env.marketBasedAuctionStartPriceOffsetMajor
			: Env.marketBasedAuctionStartPriceOffsetNonMajor;
	},
};
